import { useLocation } from "react-router-dom";
import { Application } from "@/contexts/ApplicationContext";
import { useActiveJob } from "@/contexts/ActiveJobContext";

// Path to stage mapping for routing
export const PATH_TO_STAGE_MAP: { [key: string]: string } = {
  "/basic-details": "basic-details",
  "/sjt": "sjt",
  "/driver-onroad-test": "driver-onroad-test",
  "/documents": "documents",
  "/screening": "screening",
  "/orientation": "orientation",
  "/orientation-quiz": "orientation-quiz",
  "/personal-details": "personal-details",
  "/trial": "trial",
  "/contract": "contract",
  "/vehicle-collection": "vehicle-collection",
  "/complete": "complete",
  "/rejected": "rejected",
  "/hold": "hold",
  "/pending": "pending",
};

/**
 * Combined hook for managing application stages, progress, and navigation
 */
export const useApplicationStage = (application: Application) => {
  const location = useLocation();
  const { job } = useActiveJob();

  // Get all stage names from job
  const allStages = job?.stages.map((stage) => stage.id) || [];

  // Determine current stage from URL first, then fallback to application context
  const currentStageFromPath = PATH_TO_STAGE_MAP[location.pathname];
  const effectiveCurrentStage =
    currentStageFromPath || application.currentStage;

  // Find the index of the current stage in our ordered stages
  const currentStageIndex = allStages.indexOf(effectiveCurrentStage as string);

  // Calculate completed stages based on both progress and form data
  const getCompletedStages = (): string[] => {
    const completedStages: string[] = [];

    // Add stages based on current position (all stages before current are completed)
    if (currentStageIndex > 0) {
      completedStages.push(...allStages.slice(0, currentStageIndex));
    }

    // Mark stages as completed based on actual form data
    if (
      application.applicationId &&
      !completedStages.includes("basic-details")
    ) {
      completedStages.push("basic-details");
    }

    // SJT is completed if score exists
    if (
      application.assessmentResults.sjtScore > 0 &&
      !completedStages.includes("sjt")
    ) {
      completedStages.push("sjt");
    }

    // Driver Onroad Test is completed if score exists
    if (
      application.assessmentResults.driverOnroadTestScore &&
      application.assessmentResults.driverOnroadTestScore > 0 &&
      !completedStages.includes("driver-onroad-test")
    ) {
      completedStages.push("driver-onroad-test");
    }

    // DVLA is completed if share code exists
    if (
      application.documents.drivingLicenceNumber &&
      !completedStages.includes("dvla")
    ) {
      completedStages.push("dvla");
    }

    // RTW is completed if passport/birth certificate exists
    if (
      application.documents.passportOrBirthCertificate &&
      !completedStages.includes("rtw")
    ) {
      completedStages.push("rtw");
    }

    // Orientation is completed if date exists
    if (
      application.orientation.date &&
      !completedStages.includes("orientation")
    ) {
      completedStages.push("orientation");
    }

    // Orientation quiz is completed if quiz score exists
    if (
      application.orientation.quizScore &&
      !completedStages.includes("orientation-quiz")
    ) {
      completedStages.push("orientation-quiz");
    }

    // Personal details is completed if personal details exist
    if (
      application.personalDetails &&
      !completedStages.includes("personal-details")
    ) {
      completedStages.push("personal-details");
    }

    // Trial is completed if vehicle release form is completed
    if (
      application.formsCompleted.vehicleReleaseForm &&
      !completedStages.includes("trial")
    ) {
      completedStages.push("trial");
    }

    // Contract is completed if driver contract is completed
    if (
      application.formsCompleted.driverContract &&
      !completedStages.includes("contract")
    ) {
      completedStages.push("contract");
    }

    // Vehicle collection is completed if vehicle collection date exists
    if (
      application.vehicleCollection?.date &&
      !completedStages.includes("vehicle-collection")
    ) {
      completedStages.push("vehicle-collection");
    }

    return completedStages;
  };

  const completedStages = getCompletedStages();

  // Get status for each stage
  const getStageStatus = (
    stage: string
  ): "completed" | "current" | "upcoming" => {
    const stageIndex = allStages.indexOf(stage);

    if (completedStages.includes(stage)) {
      return "completed";
    } else if (stageIndex === currentStageIndex) {
      return "current";
    } else {
      return "upcoming";
    }
  };

  // Calculate progress percentage based on completed stages and current stage progress
  const calculateProgress = (): number => {
    if (currentStageIndex === -1 || allStages.length === 0) return 0;

    const totalStages = allStages.length;

    return Math.min((currentStageIndex / totalStages) * 100, 100);
  };

  // Get next steps based on current stage
  const getNextSteps = (): string => {
    switch (effectiveCurrentStage) {
      case "basic-details":
        return "Complete your basic details to continue your application.";
      case "sjt":
        return "Complete the Situational Judgment Test to continue your application.";
      case "driver-onroad-test":
        return "Complete the Driver Onroad Test to continue your application.";
      case "documents":
        return "Submit your documents for verification.";
      case "screening":
        return "We will contact you to schedule your screening call. No action needed right now.";
      case "orientation":
        if (!application.orientation.date) {
          return "Schedule and attend your orientation session.";
        }
        return "Attend your scheduled orientation session.";
      case "orientation-quiz":
        return "Complete your orientation quiz.";
      case "personal-details":
        return "Complete your personal information to finalize your application.";
      case "trial":
        return "Complete your trial period requirements including the vehicle release form.";
      case "contract":
        return "Sign your driver contract to continue.";
      case "vehicle-collection":
        return "Complete payment information and schedule your vehicle collection appointment.";
      case "completed":
        return "Your application has been completed.";
      case "rejected":
        return "Your application has been rejected.";
      case "hold":
        return "Your application is on hold.";
      case "pending":
        return "Your application is pending review.";
      default:
        return "Your application is being processed.";
    }
  };

  // Get URL for continuing application
  const getContinueUrl = (): string => {
    switch (effectiveCurrentStage) {
      case "basic-details":
        return "/basic-details";
      case "sjt":
        return "/sjt";
      case "driver-onroad-test":
        return "/driver-onroad-test";
      case "documents":
        return "/documents";
      case "screening":
        return "/screening";
      case "orientation":
        if (application.orientation.date) {
          return "/orientation-confirmation";
        }
        return "/orientation";
      case "orientation-quiz":
        return "/orientation-quiz";
      case "personal-details":
        return "/personal-details";
      case "trial":
        return "/trial";
      case "contract":
        return "/contract";
      case "vehicle-collection":
        return "/vehicle-collection";
      case "complete":
        return "/complete";
      case "rejected":
        return "/rejected";
      case "hold":
        return "/hold";
      case "pending":
        return "/pending";
      default:
        return "/";
    }
  };

  return {
    // Stage management
    currentStage: effectiveCurrentStage,
    currentStageIndex,
    completedStages,
    getStageStatus,
    allStages,

    // Progress management
    progress: calculateProgress(),
    getNextSteps,
    getContinueUrl,
  };
};
